import { zodResolver } from '@hookform/resolvers/zod'
import { loginRequestSchema, type LoginRequest } from '@console/types'
import { useForm } from 'react-hook-form'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useLoginMutation } from '../auth/queries.js'
import { AuthCard, authCardStyles as styles } from '../auth-shell/AuthCard.js'
import { ApiError } from '../api.js'
import { Button, Input } from '../ui/index.js'

interface FromState {
  from?: { pathname: string }
}

/**
 * `loginRequestSchema` is the same zod schema the backend validates the
 * request body against; a mismatch between what this form collects and what
 * the route accepts would be a compile-time type error here, not a runtime
 * surprise.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * Operator-console UX audit, presentation only — the form's own schema,
 * validation and submit call are untouched. What changed:
 *
 * This screen's own comment used to read *"Deliberately unstyled beyond
 * legibility — the design register (UI spec §8) is Phase 3b's work, not this
 * one's."* Phase 3b shipped a session ago; nothing came back to update the
 * one screen every operator meets before any of it. Rebuilt on `AuthCard`,
 * the same shell the customer console's own login now uses — one gap, one
 * fix, both sides of this codebase.
 * ───────────────────────────────────────────────────────────────────────────
 */
export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const mutation = useLoginMutation()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginRequest>({ resolver: zodResolver(loginRequestSchema) })

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, {
      onSuccess: () => {
        const state = location.state as FromState | null
        navigate(state?.from?.pathname ?? '/', { replace: true })
      },
    })
  })

  return (
    <AuthCard documentTitle="Sign in · Pruvato" title="Sign in" lede="The operations console.">
      <form
        className={styles['form']}
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          autoFocus
          {...register('email')}
          {...(errors.email && { error: errors.email.message })}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          {...register('password')}
          {...(errors.password && { error: errors.password.message })}
        />

        <div className={styles['actions']}>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            {mutation.isPending ? 'Signing in…' : 'Sign in'}
          </Button>
          <Link to="/reset-password" className={styles['quietAction']}>
            Forgot your password?
          </Link>
        </div>

        {mutation.isError && (
          <p role="alert" className={styles['error']}>
            {mutation.error instanceof ApiError
              ? mutation.error.message
              : 'That could not be sent.'}
          </p>
        )}
      </form>
    </AuthCard>
  )
}
